// ═════════════════════════════════════════════════════════════════════════════
// Mack · CASCADE tab (build-06) — THE CRESCENDO
//
// SOURCE: scratchpad/mack_design_refs/build-06_tab-07_cascade.png
// Colours INHERIT build-01 tokens + C.teal. Nothing new.
//
// Every layer converges here: who is most likely behind this move, which
// direction, how fast, how long, and what evidence would change the answer.
//
// TWO PRINCIPLES, both easy to blur:
//
//   ELIMINATION IS A HARD CONSTRAINT. RANKING IS A HYPOTHESIS.
//   "There are no central banks in indices" is a fact about market structure.
//   "Most consistent with sovereign wealth" is a ranked hypothesis carrying a
//   confidence band. The output must never present one as the other.
//
//   CONFIDENCE IS A BAND, NOT A PERCENTAGE.
//   High · Moderate · Low · Contested. A percentage implies a base of hundreds
//   of observations that does not exist yet. Calibration reads "not yet
//   measured" for the same reason — a Brier score off twenty observations is an
//   anecdote with decimals.
//
// THE WEIGHTING:
//   W = M × R × A × S × P × (B·F) × sign
//
// P is a SCALAR, never a gate: central banks and sovereign funds do not keep
// session hours, and a binary gate would zero a tier that is genuinely present
// through standing orders.
//
// Counterparty-sheet contributions enter with the sign REVERSED — for a currency
// pair, strength on the quote side is weakness in the pair.
//
// REUSE, NOT A THIRD COPY: M and S are read from Phases' M matrix, and R from
// Flow's capacity computation for this instrument. All three verified against
// the design — see the report. Only A, P, B·F and the sign are Cascade's own.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import { C, MONO } from './tokens';
import { card, panelTitle, panelNote, outlinePill, microLabel, Chip } from './primitives';
import { MACK_M_WEIGHTS, MACK_PHASE_SHEETS } from './PhasesTab';
import { MACK_FLOW_INSTRUMENTS, computeCapacity } from './FlowTab';
import { classifyStage } from './stages';
import { MACK_CALC_TIERS } from './CalculatorTab';
import { BLOCK_ORDER, strongestBlock } from './blocks';

// ── TYPES ────────────────────────────────────────────────────────────────────

export interface MackCascadeRow {
  tier: string;
  sheet_code: string;        // keys into Phases' M matrix
  sheet_label: string;       // as the design prints it
  A: number;                 // activation of this sheet for the tier's block domain
  P: number;                 // session presence — scalar, never a gate
  // DERIVED when omitted (build-10): B·F is the tier's Calculator sensitivity
  // dotted with the sheet's Phases vector. Stored only on the design instrument,
  // where the printed values are transcription and must stay verbatim.
  BdotF?: number;
  counterparty: boolean;     // true → sign reversed
  // NO M / S / R / W FIELDS. M and S are read from Phases, R from Flow's
  // capacity band, and W is the product. Storing any of them would let this tab
  // disagree with the surfaces that produce them.
}

export interface MackContextTier { tier: string; why_never_the_answer: string }
export interface MackPrecedent { episode: string; attribution: string; ran: string; held: string; failed?: boolean; class: 'Reconstructed' | 'Lived' }
export interface MackArcEntry { date: string; attribution: string; tone: 'green' | 'amber'; note: string; outcome: string }

export interface MackCascadeInstrument {
  instrument: string;
  // NO sheets_in_contest / dominant_block / implied_side FIELDS (build-10).
  // All three derive: the contest line from the rows' sheets, the dominant block
  // from the leading tier's sheet vector, and the side from whether the leading
  // tier sits on the counterparty sheet. Deriving them corrected two rows that
  // had drifted from what Overview's design shows — see the report.
  rows: MackCascadeRow[];
  discriminator: string;      // genuine prose — the one field that cannot derive
  new_attribution: boolean;
  from_design: boolean;
  // NO context_tiers / speed / review_point / cycle_line / new_attribution_note.
  // All five derive (build-10) and the design proves each: speed is the leading
  // tier's Phases speed, the review point follows the speed class, the cycle line
  // is the leading tier's sheet cycle, the context tiers follow the asset class
  // (no central banks in indices, pensions invisible in FX tape, dealers always
  // neutral), and the note has exactly two variants.
}

// ── CONFIDENCE ───────────────────────────────────────────────────────────────
// A BAND, never a percentage. The band thresholds are not shown in the design —
// the design's own inputs (sep 27%, agr 89%) give 0.728 and it prints "High", so
// the High floor sits at or below 0.728. The rest of the ladder is INFERRED.
const CONFIDENCE_FLOOR = 0.35;
const CONFIDENCE_CAP = 0.85;

function confidenceBand(score: number): string {
  if (score >= 0.70) return 'High';
  if (score >= 0.55) return 'Moderate';
  if (score >= 0.45) return 'Low';
  return 'Contested';
}

const BAND_COLOUR: Record<string, string> = {
  High: C.green, Moderate: C.amber, Low: C.amberState, Contested: C.red,
};

// ── COMPUTATION ──────────────────────────────────────────────────────────────

export interface WeightedRow extends MackCascadeRow {
  M: number; S: number; R: number; BdotF: number; W: number; pct: number;
}

// B·F when a row does not carry one: the tier's Calculator sensitivity dotted
// with the sheet's Phases vector. This is the actual definition of B·F, so
// deriving it is closer to the truth than storing it — Cascade only stores the
// design instrument's values because those are a transcription.
function deriveBdotF(tier: string, sheetCode: string): number {
  const t = MACK_CALC_TIERS.find(x => x.name === tier);
  const sheet = MACK_PHASE_SHEETS.find(x => x.sheet_code === sheetCode);
  if (!t || !sheet) return 0;
  let num = 0, den = 0;
  BLOCK_ORDER.forEach(b => {
    const w = t.sensitivity[b] / 3;
    num += w * (sheet.block_vector[b] ?? 0);
    den += w;
  });
  return den > 0 ? num / den : 0;
}

export interface CascadeResult {
  weighted: WeightedRow[];       // sorted by |W| descending
  totalAbs: number;
  separation: number;
  agreement: number;
  confidenceScore: number;
  confidence: string;
  capacityMultiple: number;
  capacityBandLabel: string;
  R: number;
  assetClass: string;
  // Derived in build-10, previously stored on the instrument.
  dominantBlock: string;
  impliedSide: string;
  sheetsInContest: string;
  speed: string;
  reviewPoint: string;
  cycleLine: string;
  contextTiers: MackContextTier[];
  newAttributionNote: string;
}

export function computeCascade(inst: MackCascadeInstrument): CascadeResult {
  // R comes from Flow's capacity band for this instrument — the same function
  // that renders the Flow tab, at its defaults (VIX 17.4, target 1.0%).
  const flow = MACK_FLOW_INSTRUMENTS.find(i => i.symbol === inst.instrument);
  const cap = computeCapacity(flow?.sigma_daily ?? 1.0, 17.4, 1.0);
  const R = cap.band.R;

  const weighted: WeightedRow[] = inst.rows.map(r => {
    // M and S are read from Phases' matrix — never re-declared here.
    const m = MACK_M_WEIGHTS.find(w => w.tier === r.tier);
    const M = m?.M[r.sheet_code] ?? 0;
    const S = m?.seasonal_multiplier ?? 1;
    const sign = r.counterparty ? -1 : 1;
    const BdotF = r.BdotF ?? deriveBdotF(r.tier, r.sheet_code);
    const W = M * R * r.A * S * r.P * BdotF * sign;
    return { ...r, M, S, R, BdotF, W, pct: 0 };
  }).sort((a, b) => Math.abs(b.W) - Math.abs(a.W));

  const totalAbs = weighted.reduce((t, r) => t + Math.abs(r.W), 0);
  weighted.forEach(r => { r.pct = totalAbs > 0 ? Math.abs(r.W) / totalAbs : 0; });

  const w1 = weighted[0], w2 = weighted[1];
  const separation = w1 && w2 && Math.abs(w1.W) > 0
    ? (Math.abs(w1.W) - Math.abs(w2.W)) / Math.abs(w1.W) : 0;
  const agreeing = w1
    ? weighted.filter(r => Math.sign(r.W) === Math.sign(w1.W)).reduce((t, r) => t + Math.abs(r.W), 0) : 0;
  const agreement = totalAbs > 0 ? agreeing / totalAbs : 0;

  const raw = 0.50 + 0.35 * separation + 0.15 * agreement;
  const confidenceScore = Math.min(CONFIDENCE_CAP, Math.max(CONFIDENCE_FLOOR, raw));

  // The dominant block is the leading tier's sheet's strongest block, and the
  // side follows from whether that tier sits on the counterparty sheet — strength
  // on the quote side is weakness in the pair.
  const leadSheet = w1 ? MACK_PHASE_SHEETS.find(s => s.sheet_code === w1.sheet_code) : undefined;
  const dominantBlock = leadSheet ? strongestBlock(leadSheet.block_vector) : '—';
  const base = inst.instrument.includes('/') ? inst.instrument.split('/')[0] : inst.instrument;
  const impliedSide = w1 ? `${w1.counterparty ? 'Short' : 'Long'} ${base}` : '—';
  const uniq = (a: string[]) => Array.from(new Set(a));
  const home = uniq(inst.rows.filter(r => !r.counterparty).map(r => r.sheet_label));
  const quote = uniq(inst.rows.filter(r => r.counterparty).map(r => r.sheet_label));
  // Speed is the leading tier's own speed off the Phases matrix; the review
  // point follows the speed class, not the block horizon — the design shows all
  // five slow-tier reads at 4–8 weeks regardless of their dominant block.
  const leadSpeed = w1 ? MACK_M_WEIGHTS.find(m => m.tier === w1.tier)?.speed ?? 'slow' : 'slow';
  const speed = leadSpeed === 'slow' ? 'Slow · structural'
              : leadSpeed === 'moderate' ? 'Moderate' : 'Fast';
  const reviewPoint = leadSpeed === 'slow' ? '4–8 weeks'
                    : leadSpeed === 'moderate' ? '3–6 weeks' : '1–3 weeks';
  const cycleLine = leadSheet
    ? `cycle · ${leadSheet.cycle} · ${leadSheet.cycle_weeks}w · ${leadSheet.cycle_state}`
    : 'cycle · —';
  const assetClass = flow?.asset_class ?? '—';
  const contextTiers: MackContextTier[] = [
    ...(assetClass === 'Index'
      ? [{ tier: 'Central Banks', why_never_the_answer: 'no central banks in indices' }] : []),
    ...(assetClass === 'FX'
      ? [{ tier: 'Pensions', why_never_the_answer: 'invisible in FX tape' }] : []),
    { tier: 'Dealers', why_never_the_answer: 'hedging, no view' },
  ];
  const newAttributionNote = inst.new_attribution
    ? 'Fresh call. Dominant block, tier or discriminator state has moved since the last read, '
      + 'so this counts as one independent observation.'
    : 'Continuation. Dominant block, tier and discriminator unchanged since the last read.';

  const sheetsInContest = quote.length
    ? `${home.join(' + ')} + ${quote.join(' + ')} (quote)`
    : `${home.join(' + ')} only`;

  return {
    weighted, totalAbs, separation, agreement, confidenceScore,
    confidence: confidenceBand(confidenceScore),
    capacityMultiple: cap.multiple_shown,
    capacityBandLabel: cap.band.label,
    R,
    assetClass,
    dominantBlock, impliedSide, sheetsInContest,
    speed, reviewPoint, cycleLine, contextTiers, newAttributionNote,
  };
}

// ── SHARED INPUTS FOR THE ADDED INSTRUMENTS ──────────────────────────────────
// Per-sheet activation, reusing the values THIS FILE already carried on its
// original six rather than inventing fresh ones. HELD CONFLICT: the store holds
// 0.15 for every sheet while Cascade's design shows live figures — held since
// build-06 and still held.
const SHEET_A: Record<string, number> = {
  USD: 0.28, GBP: 0.23, EUR: 0.41, JPY: 1.00, AUD: 0.20, BLOC: 1.00,
};

// Tiers present in BOTH rosters — Phases supplies M and S, Calculator supplies
// the sensitivity B·F derives from. The roster conflict between them is a HELD
// item, so the added instruments use the intersection rather than resolving it.
const SHARED_TIERS = ['Corporates', 'Central Banks', 'Pensions / Family',
                      'Institutional', 'Large Banks', 'Hedge Funds', 'Sovereign Wealth'];

// Sheet membership follows the rule Inputs already encodes: an FX pair contests
// its base sheet against its quote sheet (quote reversed); an index contests its
// home sheet alone; a USD-priced commodity contests the US sheet alone unless it
// also has an official-sector sheet, which only gold does.
function rowsFor(spec: { code: string; counterparty: boolean }[]): MackCascadeRow[] {
  const out: MackCascadeRow[] = [];
  spec.forEach(({ code, counterparty }) => {
    const label = MACK_PHASE_SHEETS.find(s => s.sheet_code === code)?.display_label ?? code;
    SHARED_TIERS.forEach(tier => {
      out.push({
        tier, sheet_code: code, sheet_label: label,
        A: SHEET_A[code] ?? 0.15,
        // Slow, non-session-bound tiers hold presence off-session; see chain 05.
        P: tier === 'Sovereign Wealth' ? 0.65 : 1.00,
        counterparty,
      });
    });
  });
  return out;
}

// ── PLACEHOLDER DATA ─────────────────────────────────────────────────────────
// GBP/JPY is TRANSCRIBED VERBATIM from the design — all nine weighted rows with
// their A, P and B·F, the context tiers, the discriminator, speed, review point
// and implied side. M and S are NOT carried here; they resolve from Phases and
// match the design's printed values exactly on all nine rows.
//
// ROSTER: the PHASES roster is used, because it is the one that carries every
// tier the design names (Macro Funds in particular, which the Calculator roster
// lacks). The Calculator/Phases roster conflict is a KNOWN HELD ITEM and is not
// resolved here — flagged in the report.
//
// A NOTE ON A: the design's JPY rows carry A = 1.00, while Calendar, Calculator
// and Phases all render JPY activation at the 0.15 store floor per the standing
// ruling. Both are transcriptions of their own designs. Same family as the held
// reactive-vs-floor item — flagged, not resolved.
export const MACK_CASCADE_INSTRUMENTS: MackCascadeInstrument[] = [
  {
    // THE DESIGN INSTRUMENT. Its nine rows keep their transcribed A, P and B·F
    // verbatim — those figures are a reading of the image, not a computation.
    instrument: 'GBP/JPY', from_design: true,
    rows: [
      { tier: 'Corporates',       sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.71, counterparty: true },
      { tier: 'Institutional',    sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.56, counterparty: true },
      { tier: 'Central Banks',    sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.60, counterparty: true },
      { tier: 'Large Banks',      sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.53, counterparty: true },
      { tier: 'Macro Funds',      sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.56, counterparty: true },
      { tier: 'Hedge Funds',      sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.52, counterparty: true },
      { tier: 'Sovereign Wealth', sheet_code: 'JPY', sheet_label: 'JPY', A: 1.00, P: 1.00, BdotF: 0.59, counterparty: true },
      { tier: 'Large Banks',      sheet_code: 'GBP', sheet_label: 'UK',  A: 0.23, P: 1.00, BdotF: 0.39, counterparty: false },
      { tier: 'Hedge Funds',      sheet_code: 'GBP', sheet_label: 'UK',  A: 0.20, P: 1.00, BdotF: 0.46, counterparty: false },
    ],
    discriminator:
      'Value migration. If control keeps building in the read over the next two weeks this is ' +
      'corporates accumulation; if price pushes and rejects back into the prior range it was ' +
      'fast money and the attribution moves to Hedge Funds.',
    new_attribution: true,
  },

  // ── THE OTHER THIRTEEN (ruling 4) ──────────────────────────────────────────
  // Sheet membership follows the rule Inputs already encodes; rows are built by
  // rowsFor, so A, P and B·F come from the shared inputs and the two matrices
  // rather than being written out. Only the discriminator is prose.
  { instrument: 'USD/JPY', from_design: false,
    rows: rowsFor([{ code: 'USD', counterparty: false }, { code: 'JPY', counterparty: true }]),
    discriminator: 'Carry unwind. If the move persists through the Tokyo fix this is repatriation; if it fades it was macro positioning.',
    new_attribution: true },

  { instrument: 'EUR/USD', from_design: false,
    rows: rowsFor([{ code: 'EUR', counterparty: false }, { code: 'USD', counterparty: true }]),
    discriminator: 'Reserve rebalancing. If the bid persists through European fixes this is official-sector; if it is session-bound it was speculative.',
    new_attribution: false },

  { instrument: 'GBP/USD', from_design: false,
    rows: rowsFor([{ code: 'GBP', counterparty: false }, { code: 'USD', counterparty: true }]),
    discriminator: 'Rate differential. If the move tracks the gilt-Treasury spread this is real-money reallocation; if it decouples it was positioning.',
    new_attribution: false },

  { instrument: 'AUD/JPY', from_design: false,
    rows: rowsFor([{ code: 'AUD', counterparty: false }, { code: 'JPY', counterparty: true }]),
    discriminator: 'Risk proxy. If the move holds while equities are flat this is a genuine carry bid; if it only moves with the index it was beta.',
    new_attribution: false },

  { instrument: 'EUR/GBP', from_design: false,
    rows: rowsFor([{ code: 'EUR', counterparty: false }, { code: 'GBP', counterparty: true }]),
    discriminator: 'Relative policy. If the move survives both central bank windows this is a rate-path repricing; if it reverses after one it was event positioning.',
    new_attribution: false },

  { instrument: 'XAU/USD', from_design: false,
    rows: rowsFor([{ code: 'BLOC', counterparty: false }, { code: 'USD', counterparty: true }]),
    discriminator: 'Physical premium. If SGE holds a premium while price grinds this is official-sector accumulation; if the premium collapses it was macro positioning.',
    new_attribution: false },

  { instrument: 'Brent', from_design: false,
    rows: rowsFor([{ code: 'USD', counterparty: false }]),
    discriminator: 'Curve shape. If backwardation deepens with the move this is physical tightness; if the curve is unchanged it was financial flow.',
    new_attribution: false },

  { instrument: 'WTI', from_design: false,
    rows: rowsFor([{ code: 'USD', counterparty: false }]),
    discriminator: 'Cushing draw. If inventories fall with the move this is physical; if they build it was positioning and the attribution moves down one tier.',
    new_attribution: false },

  { instrument: 'Nasdaq 100', from_design: false,
    rows: rowsFor([{ code: 'USD', counterparty: false }]),
    discriminator: 'Breadth against concentration. If the advance broadens this is allocation; if it stays in a handful of names it was systematic.',
    new_attribution: false },

  { instrument: 'S&P 500', from_design: false,
    rows: rowsFor([{ code: 'USD', counterparty: false }]),
    discriminator: 'Participation. If the advance holds with breadth and steady volume this is allocation; if volume thins into it, it was drift.',
    new_attribution: false },

  { instrument: 'Dow', from_design: false,
    rows: rowsFor([{ code: 'USD', counterparty: false }]),
    discriminator: 'Rotation. If cyclicals lead the move this is a genuine cycle read; if it is defensive-led it was risk reduction wearing a rally.',
    new_attribution: false },

  { instrument: 'DAX', from_design: false,
    rows: rowsFor([{ code: 'EUR', counterparty: false }]),
    discriminator: 'Breadth. If the advance holds with breadth this is institutional allocation; if it narrows to a handful of names it was systematic flow.',
    new_attribution: false },

  { instrument: 'FTSE 100', from_design: false,
    rows: rowsFor([{ code: 'GBP', counterparty: false }]),
    discriminator: 'Currency offset. If the index rises while sterling falls this is translation, not demand; if both rise it is a real domestic bid.',
    new_attribution: false },
];

// Precedent and arc are instrument-independent history in the design; both are
// transcribed verbatim from it.
export const MACK_PRECEDENT: MackPrecedent[] = [
  { episode: 'Mar 2024 · gold accumulation', attribution: 'Central Banks · BLOC',    ran: '6 wks',  held: 'Held', class: 'Reconstructed' },
  { episode: 'Aug 2024 · gold breakout',     attribution: 'Macro Funds · USD',       ran: '9 days', held: 'Held', class: 'Reconstructed' },
  { episode: 'Nov 2024 · gold rejection',    attribution: 'Hedge Funds · USD',       ran: '2 days', held: 'Failed — was CB buying', failed: true, class: 'Reconstructed' },
  { episode: 'Feb 2025 · gold grind',        attribution: 'Sovereign Wealth · BLOC', ran: '5 wks',  held: 'Held', class: 'Reconstructed' },
  { episode: '12 Jun 2025 · gold',           attribution: 'Sovereign Wealth · BLOC', ran: '4 wks',  held: 'Held', class: 'Lived' },
  { episode: '09 Jul 2025 · gold',           attribution: 'Central Banks · BLOC',    ran: '—',      held: 'Open', class: 'Lived' },
];

export const MACK_ARC: MackArcEntry[] = [
  { date: '12 Jun', attribution: 'Sovereign',    tone: 'green', note: 'Both channels aligned, value migrating.',                                 outcome: '+2.1R' },
  { date: '26 Jun', attribution: 'Sovereign',    tone: 'green', note: 'Held. Control building higher.',                                          outcome: '+0.4R' },
  { date: '09 Jul', attribution: 'Macro',        tone: 'amber', note: 'Signature turned faster. Reattributed on the discriminator, not the tape.', outcome: '−0.3R' },
  { date: '18 Jul', attribution: 'Sovereign',    tone: 'green', note: 'Migration resumed post-FOMC. Reattributed back.',                          outcome: '+1.4R' },
  { date: '25 Jul', attribution: 'Central Bank', tone: 'green', note: 'Current. A = 1.00 on the bloc sheet.',                                     outcome: 'open' },
];

// ── GRIDS — measured (content 1008px → ×1.468 → 1480 CSS) ────────────────────
const WEIGHT_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1fr) 90px 74px 60px 74px 74px 64px 74px 92px',
  alignItems: 'center',
};
const PRECEDENT_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) 165px 60px 130px 110px', alignItems: 'center',
};
const ARC_GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '58px 96px minmax(0,1fr) 62px', alignItems: 'center',
};

// The ranked-tier bar's track represents this share, not 100%. Read off the
// design: 51px track, 23% row -> 33px fill, 8% row -> 12px fill. Both give a
// ceiling of ~0.35. Fill stays PROPORTIONAL TO THE ROW'S OWN percentage, so
// doubling the share doubles the bar. FLAGGED: a 23% row still fills two thirds
// of its track, so the bar reads as rank position rather than as the share
// itself — that is the design's treatment, not a correction of it.
const RANK_BAR_CEILING = 0.35;

const monoSmall: React.CSSProperties = { fontSize: 11.5, fontFamily: MONO };

// ── TAB ──────────────────────────────────────────────────────────────────────
//
// INTERACTION: the instrument selector, and nothing else. The design shows no
// hover treatment on the chain layers, no expandable rank rows and no selectable
// precedent episode. Stated rather than invented.

export function CascadeTab() {
  const [instrument, setInstrument] = useState('GBP/JPY');
  const inst = useMemo(
    () => MACK_CASCADE_INSTRUMENTS.find(i => i.instrument === instrument) ?? MACK_CASCADE_INSTRUMENTS[0],
    [instrument],
  );
  const c = useMemo(() => computeCascade(inst), [inst]);

  const top = c.weighted[0];
  const ranked = c.weighted.slice(0, 5);

  const R = (v: React.ReactNode, s: React.CSSProperties, key?: string) =>
    <span key={key} style={{ ...s, textAlign: 'right' }}>{v}</span>;

  // The elimination chain derives almost everything it prints.
  const sheetStage = (code: string) => {
    const s = MACK_PHASE_SHEETS.find(x => x.sheet_code === code);
    return s ? classifyStage(s.delta_growth, s.delta_inflation) : '—';
  };
  const codes = Array.from(new Set(inst.rows.map(r => r.sheet_code)));
  const stageLine = codes.map(k => {
    const lbl = MACK_PHASE_SHEETS.find(s => s.sheet_code === k)?.display_label ?? k;
    return `${lbl} ${sheetStage(k)}`;
  }).join(' · ');
  const reactiveLine = codes.map(k => {
    const lbl = MACK_PHASE_SHEETS.find(s => s.sheet_code === k)?.display_label ?? k;
    const a = inst.rows.find(r => r.sheet_code === k)?.A ?? 0;
    return `${lbl} ${a >= 0.6 ? 'react' : 'sleep'}`;
  }).join(' · ');
  const dominantVector = MACK_PHASE_SHEETS.find(s => s.sheet_code === codes[0]);
  const routingValue = dominantVector
    ? `${c.dominantBlock} at ${(dominantVector.block_vector as any)[c.dominantBlock]?.toFixed(2) ?? '—'}`
    : c.dominantBlock;

  const CHAIN: { n: string; title: string; value: string; desc: string }[] = [
    { n: '01', title: 'Stage',       value: stageLine,
      desc: 'Classification from sign of Δgrowth against Δinflation off the live series.' },
    { n: '02', title: 'Reactive',    value: reactiveLine,
      desc: 'Activation decays exponentially on distance to the next scheduled catalyst.' },
    { n: '03', title: 'Class',       value: c.assetClass,
      desc: 'Structural elimination — these tiers cannot participate regardless of size or state.' },
    { n: '04', title: 'Capacity',    value: `${c.capacityMultiple}× · R ${c.R}`,
      desc: 'Multi-tier coalition.' },
    { n: '05', title: 'Session',     value: 'Europe',
      desc: 'Presence is a scalar. Slow tiers hold 0.65 off-session; fast tiers drop to 0.15.' },
    { n: '06', title: 'Routing',     value: routingValue,
      desc: 'Block sensitivity dotted with the sheet vector. This is where direction comes from.' },
    { n: '07', title: 'Seasonality', value: 'bounded 0.75–1.25',
      desc: 'Only tiers with non-zero rotation propensity are adjusted.' },
    { n: '08', title: 'Directional', value: `${c.weighted.length} weighted`,
      desc: 'Neutral tiers held as context and never ranked.' },
    { n: '09', title: 'Separation',  value: `${Math.round(c.separation * 100)}% · agr ${Math.round(c.agreement * 100)}%`,
      desc: 'Confidence from the gap between first and second, plus share of weight agreeing.' },
    { n: '10', title: 'Emit',        value: `${c.confidence} · ${c.reviewPoint}`,
      desc: 'Full input state logged with config hash for replay at any future calibration.' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>

      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <div style={{ ...card, padding: '19px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={panelTitle}>The crescendo</div>
            <div style={{ ...panelNote, maxWidth: 900, marginTop: 7 }}>
              Who is most likely behind this move, which direction, how fast, how long — and what
              would change the answer.
            </div>
          </div>
        </div>

        {/* FOURTEEN CHIPS WILL NOT SIT WHERE SIX DID. The design put six on the
            right of the header; fourteen at that width either squeeze the title
            or wrap into an unreadable block. Solved by moving the selector to its
            own full-width row and GROUPING BY CLASS — the list is not negotiable,
            the presentation is. Class labels are singular per ruling 3. */}
        <div style={{ display: 'flex', gap: 26, marginTop: 17, flexWrap: 'wrap' }}>
          {(['FX', 'Commodity', 'Index'] as const).map(cls => {
            const group = MACK_CASCADE_INSTRUMENTS.filter(i =>
              (MACK_FLOW_INSTRUMENTS.find(f => f.symbol === i.instrument)?.asset_class) === cls);
            if (!group.length) return null;
            return (
              <div key={cls}>
                <div style={{ ...microLabel, marginBottom: 9 }}>{cls}</div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {group.map(i => (
                    <Chip key={i.instrument} label={i.instrument}
                          active={i.instrument === instrument}
                          onClick={() => setInstrument(i.instrument)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── ATTRIBUTION + WEIGHTING ──────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 17, alignItems: 'start' }}>

        <div style={{ ...card, padding: '17px 20px 20px' }}>
          <div style={{ ...microLabel, marginBottom: 12 }}>Attribution — computed</div>

          {/* the centrepiece card, amber-edged in the design */}
          <div style={{
            background: C.formulaBg, border: `1px solid ${C.chipBorder}`,
            borderRadius: 10, padding: '17px 18px 18px',
          }}>
            <div style={{ fontSize: 19, fontWeight: 700, color: C.text }}>
              {top ? `${top.tier} — ${top.sheet_label.replace(' −', '')} resident` : '—'}
            </div>
            <div style={{ ...monoSmall, color: BAND_COLOUR[c.confidence], marginTop: 7, fontWeight: 700 }}>
              {c.confidence} confidence · ranked, not identified
            </div>
            <div style={{ ...monoSmall, color: C.textMuted, marginTop: 5 }}>
              {c.sheetsInContest} · {c.capacityBandLabel} · {c.dominantBlock} dominant
            </div>
            <div style={{ ...monoSmall, color: C.violet, marginTop: 5 }}>{c.cycleLine}</div>

            {/* ranked tiers */}
            <div style={{ marginTop: 15 }}>
              {ranked.map((r, n) => (
                <div key={`${r.tier}-${r.sheet_code}`} style={{
                  display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 75px 40px',
                  alignItems: 'center', gap: 9, padding: '9px 0',
                  borderTop: n === 0 ? 'none' : `1px solid ${C.rule}`,
                }}>
                  <span style={{ fontSize: 10, fontFamily: MONO, color: C.textMuted }}>{n + 1}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                      {r.tier} <span style={{ color: C.textMuted, fontWeight: 400, fontSize: 11 }}>{r.sheet_label.replace(' −', '')}</span>
                    </div>
                    <div style={{ fontSize: 10.5, fontFamily: MONO, color: C.textMuted, marginTop: 3 }}>
                      M {r.M.toFixed(2)} · A {r.A.toFixed(2)} · B·F {r.BdotF.toFixed(2)}
                    </div>
                  </div>
                  {/* The fill is proportional to THIS row's share, never to the
                      leader's. Measured off the design: a 51px track, with the
                      23% row filling 33px and the 8% row 12px — so the track
                      represents RANK_BAR_CEILING, not 100%, and the leader does
                      not fill it. Scaling to the leader (what build-06 shipped
                      first) pinned the top row at full length and compressed
                      everything under it. */}
                  <div style={{ height: 4, borderRadius: 2, background: '#2c251e', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(1, r.pct / RANK_BAR_CEILING) * 100}%`, height: '100%',
                      background: `linear-gradient(90deg, #8d3b38, ${C.red})`,
                      borderRadius: 2,
                    }} />
                  </div>
                  <span style={{ fontSize: 12, fontFamily: MONO, color: C.red, textAlign: 'right' }}>
                    {Math.round(r.pct * 100)}%
                  </span>
                </div>
              ))}
            </div>

            <div style={{ ...microLabel, marginTop: 16, marginBottom: 9 }}>Held as context — never the answer</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {c.contextTiers.map(t => (
                <span key={t.tier} style={{
                  fontSize: 10.5, fontFamily: MONO, color: C.blue,
                  border: `1px solid ${C.blueDim}`, borderRadius: 5, padding: '4px 9px',
                }}>
                  {t.tier} — {t.why_never_the_answer}
                </span>
              ))}
            </div>

            <div style={{ ...microLabel, marginTop: 17, marginBottom: 8 }}>Discriminator — declared before the outcome</div>
            <div style={{ fontSize: 12.5, color: C.textSub, lineHeight: 1.6 }}>{inst.discriminator}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 14, marginTop: 18 }}>
              <div>
                <div style={{ ...microLabel, marginBottom: 8 }}>Speed</div>
                <span style={{
                  fontSize: 9.5, fontFamily: MONO, letterSpacing: '0.12em', textTransform: 'uppercase',
                  border: `1px solid ${C.teal}`, color: C.teal, borderRadius: 20, padding: '4px 10px',
                }}>{c.speed}</span>
              </div>
              <div>
                <div style={{ ...microLabel, marginBottom: 8 }}>Review at</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>{c.reviewPoint}</div>
              </div>
              <div>
                <div style={{ ...microLabel, marginBottom: 8 }}>Implied side</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{c.impliedSide}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 18 }}>
              <span style={{
                fontSize: 9, fontFamily: MONO, letterSpacing: '0.12em', textTransform: 'uppercase',
                border: `1px solid ${inst.new_attribution ? C.teal : C.textMuted}`,
                color: inst.new_attribution ? C.teal : C.textMuted,
                borderRadius: 20, padding: '4px 10px', flexShrink: 0, whiteSpace: 'nowrap',
              }}>{inst.new_attribution ? 'New attribution' : 'Continuation'}</span>
              <span style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.55 }}>{c.newAttributionNote}</span>
            </div>
          </div>
        </div>

        {/* ── WEIGHTING TABLE ────────────────────────────────────────── */}
        <div style={{ ...card, padding: '17px 22px 20px', overflowX: 'auto' }}>
          <div style={{ minWidth: 700 }}>
            <div style={{ ...microLabel, marginBottom: 12 }}>Weighting — W = M × R × A × S × P × (B·F)</div>

            <div style={{ ...WEIGHT_GRID, padding: '0 0 10px', borderBottom: `1px solid ${C.rule}` }}>
              <span style={microLabel}>Tier</span>
              <span style={microLabel}>Sheet</span>
              {R('M', microLabel)}{R('R', microLabel)}{R('A', microLabel)}
              {R('S', microLabel)}{R('P', microLabel)}{R('B·F', microLabel)}{R('W', microLabel)}
            </div>

            {c.weighted.map(r => (
              <div key={`${r.tier}-${r.sheet_code}`} style={{ ...WEIGHT_GRID, minHeight: 39, borderBottom: `1px solid ${C.rule}` }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.tier}</span>
                <span style={{ ...monoSmall, color: C.textMuted }}>
                  {r.sheet_label.replace(' −', '')}{r.counterparty ? ' −' : ''}
                </span>
                {R(r.M.toFixed(2),      { ...monoSmall, color: C.textSub })}
                {R(String(r.R),         { ...monoSmall, color: C.amber })}
                {R(r.A.toFixed(2),      { ...monoSmall, color: r.A >= 0.6 ? C.green : C.textMuted })}
                {R(r.S.toFixed(2),      { ...monoSmall, color: r.S > 1 ? C.amber : C.textMuted })}
                {R(r.P.toFixed(2),      { ...monoSmall, color: r.P >= 1 ? C.green : C.textMuted })}
                {R(r.BdotF.toFixed(2),  { ...monoSmall, color: C.amber })}
                {R(`${r.W < 0 ? '−' : '+'}${Math.abs(r.W).toFixed(3)}`,
                   { ...monoSmall, fontWeight: 700, color: r.W < 0 ? C.red : C.green })}
              </div>
            ))}

            <div style={{ fontSize: 12, fontStyle: 'italic', color: C.textMuted, marginTop: 15, lineHeight: 1.6 }}>
              The dot product carries the sign; everything else scales magnitude. Counterparty-sheet
              contributions enter reversed. Neutral tiers show R = 0 and never rank.
            </div>
          </div>
        </div>
      </div>

      {/* ── PRECEDENT + ARC ──────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 17, alignItems: 'start' }}>

        <div style={{ ...card, padding: '17px 22px 20px', overflowX: 'auto' }}>
          <div style={{ minWidth: 600 }}>
            <div style={panelTitle}>Precedent — reconstructed and lived</div>
            <div style={{ ...PRECEDENT_GRID, padding: '16px 0 10px', borderBottom: `1px solid ${C.rule}` }}>
              <span style={microLabel}>Episode</span>
              <span style={microLabel}>Attribution</span>
              {R('Ran', microLabel)}
              <span style={{ ...microLabel, paddingLeft: 14 }}>Held</span>
              <span style={microLabel}>Class</span>
            </div>
            {MACK_PRECEDENT.map(p => (
              <div key={p.episode} style={{ ...PRECEDENT_GRID, minHeight: 42, borderBottom: `1px solid ${C.rule}` }}>
                <span style={{ fontSize: 12.5, color: C.textSub }}>{p.episode}</span>
                <span style={{ fontSize: 12.5, color: C.text }}>{p.attribution}</span>
                {R(p.ran, { fontSize: 12.5, fontFamily: MONO, color: C.text, fontWeight: 700 })}
                <span style={{ fontSize: 12, color: p.failed ? C.red : C.textSub, paddingLeft: 14 }}>{p.held}</span>
                <span style={{ fontSize: 12, color: p.class === 'Lived' ? C.blue : C.violet }}>{p.class}</span>
              </div>
            ))}
            <div style={{ fontSize: 11.5, fontStyle: 'italic', color: C.textMuted, marginTop: 15, lineHeight: 1.65 }}>
              The chain never needed the book. Class, capacity at that date's price, session, regime,
              activation and event context are all historically recoverable — precedent seeds from
              history rather than waiting. Reconstructions are blinded and stamped separately from
              lived calls.
            </div>
          </div>
        </div>

        <div style={{ ...card, padding: '17px 22px 20px', overflowX: 'auto' }}>
          <div style={{ minWidth: 520 }}>
            <div style={panelTitle}>The arc — nothing merges</div>
            <div style={{ marginTop: 14 }}>
              {MACK_ARC.map(a => (
                <div key={a.date} style={{ ...ARC_GRID, minHeight: 40, borderBottom: `1px solid ${C.rule}`, gap: 10 }}>
                  <span style={{ ...monoSmall, color: C.textMuted }}>{a.date}</span>
                  <span style={{ ...monoSmall, fontWeight: 700, color: a.tone === 'green' ? C.green : C.amber }}>
                    {a.attribution}
                  </span>
                  <span style={{ fontSize: 12, color: C.textSub, lineHeight: 1.5 }}>{a.note}</span>
                  {R(a.outcome, {
                    ...monoSmall,
                    color: a.outcome === 'open' ? C.textMuted : a.outcome.startsWith('−') ? C.red : C.green,
                  })}
                </div>
              ))}
            </div>

            {/* CALIBRATION IS BLANK BY DESIGN. A Brier score from twenty
                observations is an anecdote with decimals. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 18 }}>
              <div>
                <div style={microLabel}>Attribution held</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.textSub, marginTop: 6 }}>not yet measured</div>
                <div style={{ fontSize: 11, fontFamily: MONO, color: C.textMuted, marginTop: 4 }}>needs ~40 resolved</div>
              </div>
              <div>
                <div style={microLabel}>Brier · all reads</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.textSub, marginTop: 6 }}>not yet measured</div>
                <div style={{ fontSize: 11, fontFamily: MONO, color: C.textMuted, marginTop: 4 }}>needs ~40 resolved</div>
              </div>
            </div>

            <div style={{
              marginTop: 16, background: C.formulaBg, borderLeft: `3px solid ${C.amber}`,
              borderRadius: '3px 8px 8px 3px', padding: '13px 16px',
              fontSize: 12, lineHeight: 1.6, color: C.textMuted,
            }}>
              <span style={{ color: C.amberBright, fontWeight: 700 }}>Confidence is a band, not a percentage.</span>{' '}
              A percentage implies a base that does not exist yet. Once LEDGER has resolved reads,
              the separation and agreement weights fit against realised hit rates and the bands
              become numbers.
            </div>
          </div>
        </div>
      </div>

      {/* ── THE ELIMINATION CHAIN — compact grid, full width, no dead space ── */}
      <div style={{ ...card, padding: '19px 24px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ ...panelTitle, flex: 1 }}>The elimination chain</div>
          <span style={{ ...outlinePill(C.textMuted), flexShrink: 0 }}>
            Hard constraints · ranking is a hypothesis
          </span>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))',
          gap: 0, marginTop: 16,
          border: `1px solid ${C.rule}`, borderRadius: 8, overflow: 'hidden',
        }}>
          {CHAIN.map((l, n) => (
            <div key={l.n} style={{
              padding: '13px 15px 15px',
              borderLeft: n % 5 === 0 ? 'none' : `1px solid ${C.rule}`,
              borderTop: n >= 5 ? `1px solid ${C.rule}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontSize: 10, fontFamily: MONO, color: C.amberDim }}>{l.n}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{l.title}</span>
              </div>
              <div style={{ fontSize: 11, fontFamily: MONO, color: C.amber, marginTop: 6 }}>{l.value}</div>
              <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 7, lineHeight: 1.55 }}>{l.desc}</div>
            </div>
          ))}
        </div>

        {/* eliminated tiers — the design's own strip is CLIPPED by the capture,
            so only the tiers the rest of the tab names are reproduced here. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 15 }}>
          {c.contextTiers.map(t => (
            <span key={t.tier} style={{
              fontSize: 10.5, fontFamily: MONO, color: C.blue,
              border: `1px solid ${C.blueDim}`, borderRadius: 5, padding: '4px 9px',
            }}>
              {t.tier} — neutral, {t.why_never_the_answer}
            </span>
          ))}
          <span style={{
            fontSize: 10.5, fontFamily: MONO, color: C.red,
            border: `1px solid ${C.red}`, borderRadius: 5, padding: '4px 9px', opacity: 0.75,
          }}>
            CTAs — trend absent on this horizon
          </span>
        </div>
      </div>
    </div>
  );
}
